import { backendApi } from './auth0-api';

interface User {
  id: string;
  email: string;
  username: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
}

/**
 * Authentication service that manages user authentication state
 *
 * This service provides methods for:
 * - Email/password signup and login
 * - Passkey registration and authentication
 * - Token management
 * - User info retrieval
 */
class AuthService {
  private state: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null,
  };

  private listeners: Set<(state: AuthState) => void> = new Set();

  constructor() {
    // Load tokens from localStorage on initialization
    this.loadFromStorage();
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Get current auth state
   */
  getState(): AuthState {
    return { ...this.state };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getState()));
  }

  /**
   * Save token and user to localStorage
   */
  private saveToStorage(): void {
    if (this.state.token) {
      localStorage.setItem('auth_token', this.state.token);
    }
    if (this.state.user) {
      localStorage.setItem('user', JSON.stringify(this.state.user));
    }
  }

  /**
   * Load token and user from localStorage
   */
  private loadFromStorage(): void {
    const token = localStorage.getItem('auth_token');
    const userJson = localStorage.getItem('user');

    if (token && userJson) {
      this.state = {
        isAuthenticated: true,
        token,
        user: JSON.parse(userJson),
      };
      this.notifyListeners();
    }
  }

  /**
   * Clear token and user from localStorage
   */
  private clearStorage(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }

  /**
   * Set authenticated state with token and user
   */
  private setAuthenticated(token: string, user: User): void {
    this.state = {
      isAuthenticated: true,
      token,
      user,
    };

    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Sign up with email, username, and password
   */
  async signupWithEmail(email: string, username: string, password: string): Promise<void> {
    try {
      const response = await backendApi.signup(email, username, password);
      this.setAuthenticated(response.token, response.user);
    } catch (error: any) {
      throw new Error(error.message || 'Signup failed');
    }
  }

  /**
   * Log in with email and password
   */
  async loginWithEmail(email: string, password: string): Promise<void> {
    try {
      const response = await backendApi.login(email, password);
      this.setAuthenticated(response.token, response.user);
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  }

  /**
   * Register a passkey for the user using backend WebAuthn system
   */
  async registerPasskey(email: string, displayName: string): Promise<void> {
    try {
      // Step 1: Get registration options from backend
      const optionsResponse = await backendApi.getPasskeyRegistrationOptions(email, displayName);
      const { options } = optionsResponse;

      // Step 2: Create credential using browser WebAuthn API
      // Convert challenge and user.id from base64url to ArrayBuffer
      const publicKeyOptions = {
        ...options,
        challenge: this.base64urlToBuffer(options.challenge),
        user: {
          ...options.user,
          id: this.base64urlToBuffer(options.user.id),
        },
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to create passkey credential');
      }

      // Step 3: Send credential to backend for verification and storage
      const response = credential.response as AuthenticatorAttestationResponse;
      const verifyResponse = await backendApi.verifyPasskeyRegistration(email, {
        id: credential.id,
        rawId: this.bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: this.bufferToBase64url(response.clientDataJSON),
          attestationObject: this.bufferToBase64url(response.attestationObject),
        },
      });

      // Set authenticated state with received token and user
      this.setAuthenticated(verifyResponse.token, verifyResponse.user);

      console.log('Passkey registered successfully');
    } catch (error: any) {
      throw new Error(error.message || 'Passkey registration failed');
    }
  }

  /**
   * Helper to convert base64url to ArrayBuffer
   */
  private base64urlToBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Helper to convert ArrayBuffer to base64url
   */
  private bufferToBase64url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Authenticate with a passkey
   */
  async loginWithPasskey(email: string): Promise<void> {
    try {
      // Step 1: Get authentication options from backend (includes challenge)
      const optionsResponse = await backendApi.getPasskeyAuthenticationOptions(email);
      const { options } = optionsResponse;

      // Step 2: Authenticate with browser WebAuthn API
      // Convert challenge and allowCredentials IDs from base64url to ArrayBuffer
      const publicKeyOptions = {
        ...options,
        challenge: this.base64urlToBuffer(options.challenge),
        allowCredentials: options.allowCredentials?.map((cred: any) => ({
          ...cred,
          id: this.base64urlToBuffer(cred.id),
        })),
      };

      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to get passkey credential');
      }

      // Step 3: Send assertion to backend for verification and get tokens
      const response = credential.response as AuthenticatorAssertionResponse;
      const verifyResponse = await backendApi.verifyPasskeyAuthentication(email, {
        id: credential.id,
        rawId: this.bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: this.bufferToBase64url(response.clientDataJSON),
          authenticatorData: this.bufferToBase64url(response.authenticatorData),
          signature: this.bufferToBase64url(response.signature),
          userHandle: response.userHandle ? this.bufferToBase64url(response.userHandle) : null,
        },
      });

      // Set authenticated state with received token and user
      this.setAuthenticated(verifyResponse.token, verifyResponse.user);

      console.log('Passkey authentication successful');
    } catch (error: any) {
      throw new Error(error.message || 'Passkey authentication failed');
    }
  }

  /**
   * Log out the current user
   */
  logout(): void {
    this.state = {
      isAuthenticated: false,
      user: null,
      token: null,
    };

    this.clearStorage();
    this.notifyListeners();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.state.isAuthenticated;
  }

  /**
   * Get current user
   */
  getUser(): User | null {
    return this.state.user;
  }

  /**
   * Get auth token
   */
  getToken(): string | null {
    return this.state.token;
  }
}

export const authService = new AuthService();
export type { User, AuthState };
